import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PriceSelectorService } from '../../../services/price-selector.service';
import { CommonDropdownComponent } from '../../common-dropdawn/common-dropdown.component';
import { PriceService } from '../../../services/price.service';
import { ItemSelectorService } from '../../../services/item-selector.service';
import { distinctUntilChanged, finalize, map, Subject, take, takeUntil } from 'rxjs';
import { MessageService } from '../../../services/message.service';
import { OfflineService } from '../../offline/offline.service';
import { OfflineEquipmentsService } from '../../offline/offline.equipments.service';
import { OfflinePriceService } from '../../offline/offline.price.service';
import { ICommonDropdownService } from '../../common-dropdawn/typing/common-dropdown-service.interface';
import { AuthService } from '../../auth/auth.service';
import { MatButtonModule } from '@angular/material/button';
import { ItemService } from '../../../services/items.service';
import { IItemCreate } from '../../../typing/item.interface';

@Component({
    selector: 'app-order-price-form',
    templateUrl: './order-price-form.component.html',
    styleUrl: './order-price-form.component.scss',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInput,
        CommonDropdownComponent,
        ReactiveFormsModule,
        MatButtonModule,
    ],
    providers: [ItemSelectorService],
})
export class OrderPriceFormComponent implements OnInit, OnDestroy {
    @Input({required: true}) formGroup!: FormGroup;
    @Input({required: true}) discountControl!: FormControl;

    private unsubscribe$ = new Subject<void>();
    protected showAddItemForm = false;
    protected savingNewItem = false;
    protected newItemNameControl = new FormControl('');
    private selectedCategoryId: number | null = null;

    constructor(
        private priceService: PriceService,
        private itemService: ItemService,
        protected itemSelectorService: ItemSelectorService,
        protected priceSelectorService: PriceSelectorService,
        public offlineService: OfflineService,
        public offlineEquipmentsService: OfflineEquipmentsService,
        public offlinePriceService: OfflinePriceService,
        private messageService: MessageService,
        private authService: AuthService
    ) {}

    ngOnInit(): void {
        if (this.pricesControl.value && !this.customPriceControl.value && !this.priceValueControl.value) {
            this.updatePrice(this.pricesControl.value);
        }

        this.pricesControl.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.unsubscribe$)
        ).subscribe((value) =>  {
            this.selectedCategoryId = null;
            this.itemControl.reset();
            if (!value) {
                return;
            }
            this.updatePrice(value);
        });

        this.discountControl.valueChanges.subscribe(() => this.calculateTotalPrice());
        this.priceValueControl.valueChanges.subscribe(() => this.formGroup.updateValueAndValidity());
        this.quantityControl.valueChanges.subscribe(() => this.formGroup.updateValueAndValidity());
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    updatePrice(priceId: number): void {
        if(this.offlineService.offline()) {
            this.offlineEquipmentsService.additionalParams = {};
            this.offlineService.prices$.pipe(
                map((prise) =>   prise?.data.find((prise) => {
                    return prise.id === priceId;
                })),
                take(1)
            ).subscribe((prise) => {
                if (prise) {
                    this.selectedCategoryId = prise.category?.id || null;
                    this.offlineEquipmentsService.additionalParams = {
                        'category.id': prise.category?.id
                    };
                    this.customPriceControl.setValue(null);
                    this.priceValueControl.setValue(prise.price);
                    this.discountPriceValueControl.setValue(prise.discountPrice);
                }
            });
            return;
        }

        this.priceService.getPrice(priceId).pipe(take(1)).subscribe({
            next: (price) => {
                this.selectedCategoryId = price.category?.id || null;
                this.itemSelectorService.additionalParams = {
                    'filter.category.id': price.category?.id
                };
                this.customPriceControl.setValue(null);
                this.priceValueControl.setValue(price.price);
                this.discountPriceValueControl.setValue(price.discountPrice);
            },
            error: (error) => this.messageService.errorMessage(error)
        });
    };

    calculateTotalPrice(): number {
        const customPrice = this.customPriceControl.value;
        let price = customPrice ?? this.priceValueControl.value ?? 0;
        if (this.discountControl.value && this.discountPriceValueControl.value !== null && this.discountPriceValueControl.value !== undefined) {
            price = customPrice ?? this.discountPriceValueControl.value ?? 0;
        }

        const quantity = this.quantityControl.value || 0;
        return Number((price * quantity).toFixed(2));
    }

    get pricesControl(): FormControl {
        return this.formGroup.get('priceId') as FormControl;
    }

    get itemControl(): FormControl {
        return this.formGroup.get('itemId') as FormControl;
    }

    get priceValueControl(): FormControl {
        return this.formGroup.get('priceValue') as FormControl;
    }

    get discountPriceValueControl(): FormControl {
        return this.formGroup.get('discountPriceValue') as FormControl;
    }

    get customPriceControl(): FormControl {
        return this.formGroup.get('customPrice') as FormControl;
    }

    get quantityControl(): FormControl {
        return this.formGroup.get('quantity') as FormControl;
    }

    get currentPriceService(): ICommonDropdownService {
        if (this.authService.isExecutor()) {
            return this.offlineService.offline()
                ?  this.offlinePriceService
                : this.priceSelectorService;
        }
        return this.priceSelectorService;
    }

    get currentEquipmentService(): ICommonDropdownService {
        if (this.authService.isExecutor()) {
            return this.offlineService.offline()
                ?  this.offlineEquipmentsService
                : this.itemSelectorService;
        }

        return this.itemSelectorService;
    }

    get canEditOrderPrice(): boolean {
        return this.authService.isExecutor() && !!this.authService.user().data?.company?.settings?.canExecutorChangePrice;
    }

    syncCustomPrice(): void {
        if (!this.canEditOrderPrice) {
            return;
        }

        const currentValue = this.priceValueControl.value;
        const customPrice = currentValue === '' || currentValue === null || currentValue === undefined ? null : Number(currentValue);
        this.customPriceControl.setValue(customPrice, { emitEvent: false });
        this.formGroup.updateValueAndValidity();
    }

    toggleAddItemForm(): void {
        this.showAddItemForm = !this.showAddItemForm;
        if (!this.showAddItemForm) {
            this.newItemNameControl.reset('');
        }
    }

    cancelAddItem(): void {
        this.showAddItemForm = false;
        this.newItemNameControl.reset('');
    }

    saveNewItem(): void {
        if (this.offlineService.offline()) {
            this.messageService.errorMessage('Добавление оборудования недоступно в офлайн-режиме');
            return;
        }

        if (!this.selectedCategoryId) {
            this.messageService.errorMessage('Сначала выберите работу, чтобы определить категорию оборудования');
            return;
        }

        const itemData: IItemCreate = {
            name: this.newItemNameControl.value ?? '',
            categoryId: this.selectedCategoryId,
            description: '',
        };

        this.savingNewItem = true;
        this.itemService.createItem(itemData).pipe(
            finalize(() => {
                this.savingNewItem = false;
            })
        ).subscribe({
            next: (createdItem) => {
                this.itemControl.setValue(createdItem.id);
                this.cancelAddItem();
                this.messageService.successMessage('Оборудование добавлено');
            },
            error: (error) => this.messageService.errorMessage(error),
        });
    }
}
